import { useState } from 'react'
import { useToast } from '../../context/ToastContext'
import { getApiBaseUrl } from '../../utils/api'

export default function UploadContentModal({ onClose, onContentAdded }) {
  const { showToast } = useToast()
  const [activeTab, setActiveTab] = useState('file')
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [urlData, setUrlData] = useState({ url: '', name: '' })
  const [addingUrl, setAddingUrl] = useState(false)

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    const file = e.dataTransfer.files?.[0]
    if (file) {
      setSelectedFile(file)
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const clearFileSelection = () => {
    setSelectedFile(null)
    const fileInput = document.getElementById('file-input')
    if (fileInput) fileInput.value = ''
  }

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB'
  }

  const uploadFile = async () => {
    if (!selectedFile) {
      showToast('error', 'Please select a file first', 'No File Selected')
      return
    }

    const authToken = localStorage.getItem('authToken')
    if (!authToken) {
      showToast('error', 'Please log in first', 'Authentication Required')
      return
    }

    try {
      setUploading(true)
      setUploadProgress(0)

      const formData = new FormData()
      formData.append('file', selectedFile)

      const xhr = new XMLHttpRequest()

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = Math.round((e.loaded / e.total) * 100)
          setUploadProgress(percentComplete)
        }
      })

      xhr.addEventListener('load', () => {
        setUploading(false)

        if (xhr.status >= 200 && xhr.status < 300) {
          const result = JSON.parse(xhr.responseText)
          showToast('success', `${selectedFile.name} uploaded successfully`, 'Upload Complete')

          if (onContentAdded) {
            onContentAdded(result.content)
          }

          setTimeout(() => {
            onClose()
          }, 1000)
        } else if (xhr.status === 409) {
          showToast('warning', 'This file already exists in your library', 'Duplicate File')
        } else {
          let errorMessage = 'Upload failed. Please try again.'
          try {
            const result = JSON.parse(xhr.responseText)
            errorMessage = result.error || errorMessage
          } catch (e) {
            // Use default message
          }
          showToast('error', errorMessage, 'Upload Failed')
        }
      })

      xhr.addEventListener('error', () => {
        setUploading(false)
        showToast('error', 'Network error. Please check your connection and try again.', 'Upload Failed')
      })

      xhr.addEventListener('abort', () => {
        setUploading(false)
        showToast('info', 'Upload was cancelled', 'Upload Cancelled')
      })

      const apiUrl = getApiBaseUrl()
      xhr.open('POST', `${apiUrl}/v1/content/upload`)
      xhr.setRequestHeader('Authorization', `Bearer ${authToken}`)
      xhr.send(formData)
    } catch (error) {
      console.error('Upload error:', error)
      setUploading(false)
      showToast('error', 'An unexpected error occurred', 'Error')
    }
  }

  const addUrlReference = async () => {
    if (!urlData.url.trim()) {
      showToast('error', 'Please enter a URL', 'Validation Error')
      return
    }

    try {
      setAddingUrl(true)
      const authToken = localStorage.getItem('authToken')
      if (!authToken) {
        showToast('error', 'Please log in first', 'Authentication Required')
        return
      }

      const apiUrl = getApiBaseUrl()
      const response = await fetch(`${apiUrl}/v1/content/reference`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          url: urlData.url.trim(),
          name: urlData.name.trim() || undefined
        })
      })

      const result = await response.json()

      if (response.ok) {
        showToast('success', 'URL reference added to your library', 'Reference Added')

        if (onContentAdded) {
          onContentAdded(result.content)
        }

        setTimeout(() => {
          onClose()
        }, 1000)
      } else if (response.status === 409) {
        showToast('warning', result.error || 'This URL already exists in your library', 'Duplicate URL')
      } else {
        showToast('error', result.error || 'Failed to add URL reference', 'Error')
      }
    } catch (error) {
      console.error('URL reference error:', error)
      showToast('error', 'Network error. Please try again.', 'Error')
    } finally {
      setAddingUrl(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Add Content</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-3xl font-light leading-none"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex mb-6 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('file')}
            className={`px-4 py-2 font-semibold border-b-2 transition-colors ${
              activeTab === 'file'
                ? 'text-primary-600 dark:text-primary-400 border-primary-600 dark:border-primary-400'
                : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-primary-600 dark:hover:text-primary-400'
            }`}
          >
            <i className="fas fa-upload mr-2"></i>Upload File
          </button>
          <button
            onClick={() => setActiveTab('url')}
            className={`px-4 py-2 font-semibold border-b-2 transition-colors ${
              activeTab === 'url'
                ? 'text-primary-600 dark:text-primary-400 border-primary-600 dark:border-primary-400'
                : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-primary-600 dark:hover:text-primary-400'
            }`}
          >
            <i className="fas fa-link mr-2"></i>Add URL Reference
          </button>
        </div>

        {/* File Upload Form */}
        {activeTab === 'file' && (
          <div className="space-y-4">
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-primary-500 dark:hover:border-primary-500 transition-colors cursor-pointer"
            >
              <i className="fas fa-cloud-upload-alt text-5xl text-gray-400 dark:text-gray-500 mb-4"></i>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Drag & drop your file here
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">or</p>
              <label
                htmlFor="file-input"
                className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg font-semibold cursor-pointer inline-block transition-colors"
              >
                Browse Files
              </label>
              <input
                type="file"
                id="file-input"
                onChange={handleFileSelect}
                className="hidden"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
                Supports: Videos, PDFs, Images, Documents
              </p>
            </div>

            {selectedFile && (
              <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <i className="fas fa-file text-blue-600 dark:text-blue-400 text-2xl mr-3"></i>
                    <div>
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {selectedFile.name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {formatFileSize(selectedFile.size)}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={clearFileSelection}
                    className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors"
                  >
                    <i className="fas fa-times text-xl"></i>
                  </button>
                </div>
              </div>
            )}

            {uploading && (
              <div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
                  <div
                    className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                  Uploading: {uploadProgress}%
                </p>
              </div>
            )}

            <button
              onClick={uploadFile}
              disabled={!selectedFile || uploading}
              className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-lg font-semibold transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Uploading...
                </>
              ) : (
                <>
                  <i className="fas fa-upload mr-2"></i>
                  Upload File
                </>
              )}
            </button>
          </div>
        )}

        {/* URL Reference Form */}
        {activeTab === 'url' && (
          <div className="space-y-4">
            <div>
              <label htmlFor="content-url-input" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Content URL
              </label>
              <input
                type="url"
                id="content-url-input"
                value={urlData.url}
                onChange={(e) => setUrlData(prev => ({ ...prev, url: e.target.value }))}
                placeholder="https://www.youtube.com/watch?v=... or https://example.com/video.mp4"
                required
                className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 dark:text-white"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Supported: YouTube videos, public video URLs, S3 URLs
              </p>
            </div>

            <div>
              <label htmlFor="content-name-input" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name (optional)
              </label>
              <input
                type="text"
                id="content-name-input"
                value={urlData.name}
                onChange={(e) => setUrlData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="My Video"
                className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent text-gray-900 dark:text-white"
              />
            </div>

            <button
              onClick={addUrlReference}
              disabled={addingUrl}
              className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-lg font-semibold transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {addingUrl ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Adding URL...
                </>
              ) : (
                <>
                  <i className="fas fa-link mr-2"></i>
                  Add URL Reference
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
